import { useEffect, useState } from 'react';
import { IconeAlerte } from '@journal/ui';
import { useSession } from '../lib/session.tsx';

/**
 * Bandeau d'information de la plateforme.
 *
 * Un message écrit au back-office et lu ici par tout le monde : maintenance
 * annoncée, incident en cours, nouveauté. C'est le seul canal qui atteint
 * quelqu'un DÉJÀ CONNECTÉ — un courriel se lit le soir, l'interruption est à
 * 14 h, au milieu d'une classe.
 *
 * ── Il doit DISPARAÎTRE tout seul ───────────────────────────────────────────
 * Un bandeau qu'on éteint au back-office et qui reste affiché jusqu'au
 * rechargement de la page est pire que pas de bandeau : il annonce une panne
 * finie, le support reçoit des appels pour un incident clos, et personne ne
 * sait qu'il suffirait d'un Ctrl+F5. La page n'est pas rechargée souvent —
 * l'application est une application d'écran, ouverte le matin et fermée le
 * soir.
 *
 * D'où l'interrogation périodique. Elle vaut dans les deux sens : allumer
 * atteint aussi les gens déjà là, ce qui est tout l'intérêt.
 */

/**
 * Une minute.
 *
 * Assez court pour qu'un message d'incident arrive avant que la personne
 * n'appelle le support, assez long pour que la requête ne pèse rien : elle lit
 * une ligne unique, sans jointure. Une minute d'écart sur une annonce de
 * maintenance ne change rien ; dix minutes, si.
 */
const PERIODE_MS = 60_000;

interface Annonce {
  message: string;
  active: boolean;
  updatedAt: string | null;
}

export function BandeauInfo() {
  const { api, etat } = useSession();
  const [annonce, setAnnonce] = useState<Annonce | null>(null);

  useEffect(() => {
    // Pas connecté : rien à lire, et la route demande un jeton.
    if (etat !== 'connecte') {
      setAnnonce(null);
      return undefined;
    }

    let vivant = true;

    async function relire() {
      try {
        const recu = await api.requete<Annonce>('/announcement');
        if (vivant) setAnnonce(recu);
      } catch {
        /*
         * ── Un échec ne dit RIEN à l'écran ─────────────────────────────────
         * Ce composant est en haut de toutes les pages et s'interroge tout
         * seul toutes les minutes. Afficher « impossible de charger
         * l'annonce » ferait apparaître une erreur au-dessus d'un travail en
         * cours, pour une information dont l'absence n'empêche rien.
         *
         * Le dernier état connu reste affiché : une coupure réseau ne doit pas
         * effacer un message de maintenance en cours.
         */
      }
    }

    void relire();
    const minuterie = setInterval(() => { void relire(); }, PERIODE_MS);

    /*
     * Et au RETOUR sur l'onglet, sans attendre le prochain tour.
     *
     * C'est le moment où l'on regarde vraiment l'écran. Laisser une minute
     * d'un bandeau périmé juste après avoir repris la main est précisément ce
     * qu'on cherche à éviter.
     */
    const auRetour = () => { if (!document.hidden) void relire(); };
    document.addEventListener('visibilitychange', auRetour);

    return () => {
      vivant = false;
      clearInterval(minuterie);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [api, etat]);

  if (!annonce?.active || annonce.message.trim() === '') return null;

  return (
    <div
      /*
       * `status` et non `alert` : le message est important mais n'interrompt
       * pas. `alert` couperait la lecture d'un lecteur d'écran en pleine
       * saisie, pour une information qui peut attendre la fin de la phrase.
       */
      role="status"
      /*
       * Nommé : la page porte déjà d'autres régions `status` — les retours
       * fugaces, par exemple. Sans nom, rien ne les distingue, ni pour un
       * lecteur d'écran ni pour un test.
       */
      aria-label="Annonce de la plateforme"
      className={[
        'flex items-start gap-2.5 border-b px-4 py-2.5 text-sm leading-snug',
        'border-[var(--color-border)] bg-[var(--color-warning-soft)]',
        'text-[var(--color-warning-soft-fg)]',
        // À l'impression, un bandeau de maintenance n'a rien à faire sur un
        // cahier journal posé devant un inspecteur.
        'print:hidden',
      ].join(' ')}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0">
        <IconeAlerte taille={16} />
      </span>
      {/* `pre-wrap` : le texte est saisi au back-office avec ses retours à la
          ligne, et les écraser collerait deux phrases sans séparation. */}
      <p className="min-w-0 flex-1 whitespace-pre-wrap break-words">
        {annonce.message}
      </p>
    </div>
  );
}
